/**
 * SF-4847 wizard step: which modules the demo shows. "Let Eva decide" (the default) or pick from
 * the modules chat-service can fill with demo data (GET .../module-options); Eva starts ticked.
 * When that list cannot be read only "Let Eva decide" is offered, which needs no list.
 */
import { Alert, Checkbox, Form, List, Radio, Space, Spin, Typography } from 'antd';
import type { DemoModuleOption } from '../api/demoBuildsClient';
import type { ModulesAnswer } from '../validation';
import { ModuleIcon } from './moduleIcons';

export interface ModulesStepProps {
  value: ModulesAnswer;
  /** The offered modules; null while loading or when the read failed. */
  options: DemoModuleOption[] | null;
  loading: boolean;
  failed: boolean;
  error: string | null;
  onChange: (next: ModulesAnswer) => void;
}

export const ModulesStep: React.FC<ModulesStepProps> = ({
  value,
  options,
  loading,
  failed,
  error,
  onChange,
}) => {
  const canChoose = !!options && options.length > 0;
  const toggle = (key: string, on: boolean) =>
    onChange({
      mode: 'chosen',
      keys: on ? [...value.keys.filter((k) => k !== key), key] : value.keys.filter((k) => k !== key),
    });
  return (
    <Form layout="vertical">
      <Form.Item
        label="Modules in the demo"
        validateStatus={error ? 'error' : undefined}
        help={error ?? 'A demo only shows modules that have demo data behind them.'}
      >
        <Radio.Group
          value={canChoose ? value.mode : 'eva_decides'}
          onChange={(e) => onChange({ ...value, mode: e.target.value })}
        >
          <Space direction="vertical">
            <Radio value="eva_decides">Let Eva decide</Radio>
            {!failed && (
              <Radio value="chosen" disabled={!canChoose}>
                Choose modules
              </Radio>
            )}
          </Space>
        </Radio.Group>
      </Form.Item>
      {loading && <Spin size="small" />}
      {failed && (
        <Alert
          type="info"
          showIcon
          data-testid="module-options-failed"
          message="The module list could not be loaded, so Eva decides which modules to show."
        />
      )}
      {canChoose && value.mode === 'chosen' && (
        <List
          size="small"
          bordered
          dataSource={options}
          rowKey={(o) => o.key}
          renderItem={(o) => (
            <List.Item data-testid="module-option">
              <Checkbox
                checked={value.keys.includes(o.key)}
                onChange={(e) => toggle(o.key, e.target.checked)}
              >
                <Space>
                  <ModuleIcon moduleKey={o.key} />
                  <Typography.Text>{o.name}</Typography.Text>
                </Space>
              </Checkbox>
            </List.Item>
          )}
        />
      )}
    </Form>
  );
};
